import { Mutex } from 'async-mutex';
const mutex = new Mutex();

const DB_NAME = 'CacheDB';
const STORE_NAME = 'cacheStore';

function isIndexedDBAvailable() {
    try {
        return typeof indexedDB !== 'undefined' && indexedDB !== null;
    } catch {
        return false;
    }
}


async function openDB() {
    if (!isIndexedDBAvailable()) {
        throw new Error('IndexedDB not supported');
    }
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, 1);

        request.onupgradeneeded = (event) => {
            const db = event.target.result;
            if (!db.objectStoreNames.contains(STORE_NAME)) {
                db.createObjectStore(STORE_NAME, { keyPath: 'key' });
            }
        };

        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
    });
}

async function getWithExpiry(key, cacheClear = undefined) {
    const db = await openDB();
    return new Promise((resolve, reject) => {
        const transaction = db.transaction(STORE_NAME, 'readonly');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.get(key);

        request.onsuccess = () => {
            const item = request.result;
            if (!item?.end || !item?.start) {
                removeItem(key);
                return resolve(null);
            }
            const clearItemTime = new Date() > new Date(item.end);
            const clearItemCache = (cacheClear) && new Date(cacheClear) > new Date(item.start);
            if (!item) return resolve(null);
            if (clearItemTime || clearItemCache) {
                removeItem(key);
                resolve(null);
            } else {
                resolve(item.value);
            }
        };

        request.onerror = () => reject(request.error);
    });
}

async function setWithExpiry(key, value, offset = 1) {
    const db = await openDB();
    return new Promise((resolve, reject) => {
        const transaction = db.transaction(STORE_NAME, 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        const now = new Date().getTime();
        const item = { key, value, start: now, end: now + offset * 60000 };

        const request = store.put(item);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
    });
}

async function removeItem(key) {
    const db = await openDB();
    return new Promise((resolve, reject) => {
        const transaction = db.transaction(STORE_NAME, 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.delete(key);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
    });
}

async function cacheFetch(url, opt) {
    return mutex.runExclusive(async () => {
        let key = url;
        if (opt.headers) {
            key += '-' + JSON.stringify(opt.headers);
        }

        const cachedResp = await getWithExpiry(key);
        if (cachedResp) {
            return cachedResp;
        } else {
            const response = await fetch(url, opt);
            const data = await response.json();
            await setWithExpiry(key, data);
            return data;
        }
    });
}

function localStorageSpace() {
    return;
}

export {
    DB_NAME,
    STORE_NAME,
    openDB,
    cacheFetch,
    setWithExpiry,
    getWithExpiry,
    removeItem,
    localStorageSpace,
    isIndexedDBAvailable,
};
